import { hsla, parseToHsla, readableColor, toHex } from "color2k";
import { HotHeatMapBaseMeta, HotHeatMapResult } from "..";



// Processes heat map data using based on the manipulation of 'isg-hm-' classes stored in the hot header column name
// Workaround based on inability to store meta data with a handsontable instance and not wishing to use global variables

// 1. Transforms the cell value to a color based on the value and the max value in the column
// 2. Retrieves the background color from a custom class name stored in the column header class name
// 3. Caches the max value in the column header class name for future use

/**
 * Handles heat map calculations for single-valued columns in Handsontable grids.
 * Manages value ranges, color scales, and storage of computed/cached values.
 */
export class HotHeatMapSingleMeta extends HotHeatMapBaseMeta {

  // ---------------- Properties  --------------------

  /**
   * The maximum value in the column.
   */
  public maxValue: number | null = null;

  /**
   * The minimum value in the column.
   */
  public minValue: number | null = null;

  /**
   * The background color for the heat map.
   */
  public colorBg: string | null = '#ffffff';

  // ---------------- Constants  --------------------

  /**
   * Key for storing the maximum value.
   */
  protected readonly keyMax: string = "isg_hm_max_v_";

  /**
   * Key for storing the minimum value.
   */
  protected readonly keyMin: string = "isg_hm_min_v_";

  /**
   * Key for storing the background color.
   */
  protected readonly keyColorBg: string = "isg_hm_colbg_";

  /**
   * Default background color for the maximum positive value.
   */
  public readonly colorDefaultBgMaxPositive: string = '#F8696B';

  // ---------------- Methods  --------------------

  /**
   * Initializes the heat map meta for a specific column and optional metadata.
   * Extracts cached values and color settings from the store.
   * @param column The column index.
   * @param meta Optional metadata string.
   */
  public init(column: number, meta?: string | null): void {

    this.store = meta ?? "";
    this.column = column;

    // Extract any precalcuated cached values from the store
    this.maxValue = this.getMax();
    this.minValue = this.getMin();

    // Extract the colors from the store
    this.colorBg = this.getColorBg();

  }

  /**
   * Initializes the value range from the provided values.
   * @param values The array of values to analyze.
   */
  public initRangeValues(values: any[]): void {

    this.setMax(values.length > 0 ? Math.max.apply(null, values) : 1);
    this.setMin(values.length > 0 ? Math.min.apply(null, values) : 0);
  }

  // ---------------- HeatMap calculation  --------------------

  /**
   * Calculates the heat map colors for a given value.
   * Returns the result with the computed foreground and background colors.
   * @param value The value to calculate colors for.
   * @returns The result containing color information.
   */
  public calcHeatmapColors(value: number | null): HotHeatMapResult {

    let result: HotHeatMapResult = new HotHeatMapResult();

    let highestValue: number = this.maxValue ?? 1;
    let lowestValue: number = this.minValue ?? 0;

    if (value == null || value == undefined) {

      this.colorHm = this.colorDefault;
      this.colorHmBg = this.colorDefaultBg;

      result.colorHm = this.colorHm;
      result.colorHmBg = this.colorHmBg;
      return result;
    }

    const [h, s, l, a] = parseToHsla(this.colorBg!);

    // Manipulate the lightness based on the value
    const maxLightness = 1 - l;

    const lightness = (this.calculatePercentagePosition(value, lowestValue, highestValue) ?? 0) * maxLightness;

    let colHsla = hsla(h, s, 1 - lightness, a);

    this.colorHmBg = toHex(colHsla);
    this.colorHm = readableColor(colHsla);

    result.colorHm = this.colorHm;
    result.colorHmBg = this.colorHmBg;
    return result;
  }

  /**
   * Removes all cached calculated values from the store, leaving only color values.
   * Resets the min/max properties to null.
   */
  public clearStore(): void {

    if (this.store == null || this.store == undefined) {
      this.store = "";
    }

    const keyMatches: string[] = [
      "\\" + this.keyMax + this.expr,
      "\\" + this.keyMin + this.expr
    ];

    for (let keyMatch of keyMatches) {

      const regex = new RegExp(keyMatch, "g");

      let matches: RegExpMatchArray | null = this.store.match(regex);

      if (matches != null && matches.length > 0) {
        this.store = this.store.replace(matches[0], "");
      }
    }

    this.store = this.store.trim();

    this.maxValue = null;
    this.minValue = null;
  }

  // ---------------- Value Accessors  --------------------

  /**
   * Gets the maximum value from the store.
   * @returns The maximum value or null.
   */
  public getMax(): number | null {

    return this.getStoreValue(this.keyMax, true);
  }

  /**
   * Gets the minimum value from the store.
   * @returns The minimum value or null.
   */
  public getMin(): number | null {

    return this.getStoreValue(this.keyMin, true);
  }

  /**
   * Sets the maximum value in the store and updates the property.
   * @param value The value to set.
   */
  public setMax(value: number | null): void {

    this.setStoreValue(value, this.keyMax);
    this.maxValue = value ?? 1;
  }

  /**
   * Sets the minimum value in the store and updates the property.
   * @param value The value to set.
   */
  public setMin(value: number | null): void {

    this.setStoreValue(value, this.keyMin);
    this.minValue = value ?? 0;
  }

  /**
   * Gets the background color from the store or returns the default.
   * @returns The background color as a string.
   */
  public getColorBg(): string | null {

    return this.getStoreValue(this.keyColorBg, false) ?? this.colorDefaultBgMaxPositive;
  }

  /**
   * Sets the background color in the store and updates the property.
   * @param value The color value to set.
   */
  public setColorBg(value: any | null): void {

    this.setStoreValue(value, this.keyColorBg);
    this.colorBg = value;
  }

}


